import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import {
  Box, Heading, Select, Flex, Input,
} from '@chakra-ui/react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { io } from 'socket.io-client';

import GenericTable from '../components/GenericTable';
import { getSummaryReport } from '../actions/summaryReportActions';
import { fetchCompanies } from '../actions/companyActions';
import CompanySelect from '../components/CompanySelect';
import PlayerActionButtons from '../components/PlayerActionButtons';
import { formatDateLocal, getLocalDateRangeForPlayerWise } from '../utils/reportDateRange';
import { useFieldPrivilege } from '../hooks/useFieldPrivilege';

const base_url = process.env.REACT_APP_API_URL_ADMIN;
const LIVE_REFRESH_INTERVAL_MS = 3000;

const filterOptions = [
  'Today',
  'Yesterday',
  'This Week',
  'Last Week',
  'This Month',
  'Custom Range',
];

const ResponsiveDateInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <Input
    ref={ref}
    value={value || ''}
    onClick={onClick}
    placeholder={placeholder}
    readOnly
    width="100%"
    bg="white"
  />
));

ResponsiveDateInput.displayName = 'ResponsiveDateInput';

const normalizeCompanyId = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value.$oid) return String(value.$oid);
  return String(value);
};

const normalizeCompanyKey = (value) => String(value || '').trim().toLowerCase();

const toLiveTicketState = (payload = {}) => {
  const gameId = payload.game_id || payload.GameID || '';
  const gameCode = payload.game_code || payload.gameCode || '';
  const drawId = payload.draw_id || payload.DrawID || payload.DrawId || '';
  const points = payload.total_points ?? payload.Points ?? payload.points;
  const cleared = Boolean(payload.live_cleared);
  const hasPoints = points === 0 || points === '0' || Boolean(points);
  if (cleared) {
    return { cleared: true };
  }
  if (!gameId && !gameCode && !hasPoints) return null;
  return {
    game_id: gameId,
    game_code: gameCode || gameId,
    draw_id: drawId ? String(drawId) : '',
    Points: points ?? '',
  };
};

const SummaryReport = () => {
  const dispatch = useDispatch();
  const socketRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { slug } = useParams();
  const { filterColumns } = useFieldPrivilege();

  const { data, loading, error } = useSelector((state) => state.summaryReport);
  const user = useSelector((state) => state.auth.user);
  const companies = useSelector((state) => state.company?.companies ?? []);

  const isSuperAdmin = user?.role === 'super_admin';

  const [filter, setFilter] = useState('Today');
  const [customRange, setCustomRange] = useState([null, null]);
  const [companyId, setCompanyId] = useState('');
  const [playerliveTkt, setPlayerliveTkt] = useState({});

  const [startDate, endDate] = useMemo(() => {
    if (filter === 'Custom Range') return customRange;
    return getLocalDateRangeForPlayerWise(filter);
  }, [filter, customRange]);

  const companyQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return normalizeCompanyKey(params.get('comp'));
  }, [location.search]);

  useEffect(() => {
    if (isSuperAdmin) dispatch(fetchCompanies());
  }, [dispatch, isSuperAdmin]);

  // Non–super-admin: use company from login (no company.view privilege needed).
  useEffect(() => {
    if (isSuperAdmin) return;
    const id = user?.companyId || user?.company_id;
    if (id) setCompanyId(String(id));
  }, [isSuperAdmin, user?.companyId, user?.company_id]);

  useEffect(() => {
    if (isSuperAdmin || !user?.companySlug || !companies.length) return;
    const match = companies.find((c) => c.slug === user.companySlug);
    if (match?._id) setCompanyId(String(match._id));
  }, [isSuperAdmin, user?.companySlug, companies]);

  useEffect(() => {
    if (!companies.length || !companyQuery) return;
    const match = companies.find((company) => {
      const slug = normalizeCompanyKey(company.slug);
      const name = normalizeCompanyKey(company.name);
      return slug === companyQuery || name === companyQuery;
    });
    if (match?._id) {
      setCompanyId(String(match._id));
    }
  }, [companies, companyQuery]);

  useEffect(() => {
    if (!isSuperAdmin || !companies.length) return;

    const params = new URLSearchParams(location.search);
    const selectedCompany = companies.find((company) => String(company._id) === String(companyId));
    const nextCompanyQuery = selectedCompany
      ? normalizeCompanyKey(selectedCompany.slug || selectedCompany.name)
      : '';

    if (nextCompanyQuery) {
      if (companyQuery === nextCompanyQuery) return;
      params.set('comp', nextCompanyQuery);
    } else {
      if (!params.has('comp')) return;
      params.delete('comp');
    }

    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
      },
      { replace: true },
    );
  }, [companyId, companies, companyQuery, isSuperAdmin, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!startDate || !endDate || !companyId) return;
    setPlayerliveTkt({});
    dispatch(getSummaryReport({ startDate, endDate, companyId }));
  }, [dispatch, startDate, endDate, companyId]);

  useEffect(() => {
    if (!Array.isArray(data) || !data.length) return;

    setPlayerliveTkt((prev) => {
      const next = { ...prev };
      let changed = false;
      const seenPlayers = new Set();

      for (const row of data) {
        if (row.type !== 'player' || !row._id) continue;
        const playerId = String(row._id);
        seenPlayers.add(playerId);

        if (!row.liveTicket) {
          if (next[playerId]) {
            delete next[playerId];
            changed = true;
          }
          continue;
        }

        const liveTicket = toLiveTicketState(row.liveTicket);
        if (!liveTicket) {
          if (next[playerId]) {
            delete next[playerId];
            changed = true;
          }
          continue;
        }

        if (liveTicket.cleared) {
          if (next[playerId]) {
            delete next[playerId];
            changed = true;
          }
          continue;
        }

        const existing = next[playerId];
        if (
          existing &&
          existing.game_code === liveTicket.game_code &&
          String(existing.Points) === String(liveTicket.Points) &&
          String(existing.draw_id || '') === String(liveTicket.draw_id || '')
        ) {
          continue;
        }

        next[playerId] = liveTicket;
        changed = true;
      }

      // Drop any local live cells for players no longer in the report payload.
      for (const playerId of Object.keys(next)) {
        if (!seenPlayers.has(playerId)) {
          delete next[playerId];
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [data]);

  const refreshData = useCallback((silent = false) => {
    if (startDate && endDate && companyId) {
      dispatch(getSummaryReport({ startDate, endDate, companyId, silent }));
    }
  }, [dispatch, startDate, endDate, companyId]);

  // Fallback soft refresh so live cells update even if the socket path is unavailable.
  useEffect(() => {
    if (!startDate || !endDate || !companyId) return undefined;
    const interval = setInterval(() => refreshData(true), LIVE_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [startDate, endDate, companyId, refreshData]);

  const applyLiveTicketUpdate = useCallback((payload) => {
    const playerId = payload?.player_id || payload?.UserID;
    if (!playerId) return;

    const eventCompanyId = normalizeCompanyId(payload?.company_id);
    if (eventCompanyId && companyId && eventCompanyId !== companyId) {
      return;
    }

    const liveTicket = toLiveTicketState(payload);
    if (!liveTicket) return;

    setPlayerliveTkt((prev) => {
      if (liveTicket.cleared) {
        if (!prev[String(playerId)]) return prev;
        const next = { ...prev };
        delete next[String(playerId)];
        return next;
      }
      return {
        ...prev,
        [String(playerId)]: liveTicket,
      };
    });
  }, [companyId]);

  useEffect(() => {
    const socket = io(base_url, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    const handleUpdate = (res) => {
      if (Array.isArray(res)) {
        res.forEach((item) => applyLiveTicketUpdate(item));
      } else {
        applyLiveTicketUpdate(res);
      }
      refreshData(true);
    };

    socket.on('connect', () => {
      console.log('Summary socket connected:', socket.id);
    });
    socket.on('reportUpdate', handleUpdate);

    return () => {
      socket.off('reportUpdate', handleUpdate);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [applyLiveTicketUpdate, refreshData]);

  const columns = useMemo(() => [
    {
      Header: 'Player',
      accessor: '_id',
      defaultWidth: 180,
      Cell: ({ row }) => {
        const { _id, type, total_player, target_perc } = row.original;
        const playerId = String(_id);
        const ticketInfo = playerliveTkt[playerId] || row.original.liveTicket;

        if (type === 'total') {
          return `${_id} ( ${total_player || 0})`;
        }

        if (type === 'superstokez') {
          const childPlayers = data.filter(
            (entry) => entry.type === 'player' && entry.playerInfo?.super_stokez_id === row.original.playerInfo?._id,
          );

          const totalPoints = childPlayers.reduce((sum, player) => {
            const playerTkt = playerliveTkt[String(player._id)] || player.liveTicket;
            return sum + (parseFloat(playerTkt?.Points) || 0);
          }, 0);

          return `${_id} ( ${total_player || 0},  ${totalPoints})`;
        }

        const gameCode = ticketInfo?.game_code || ticketInfo?.game_id || '';
        const points = ticketInfo?.Points ?? ticketInfo?.total_points ?? '';
        return (
          <Box display="flex" alignItems="center" gap="4px" flexWrap="wrap">
            <Box as="span">{`${_id}[${target_perc || 0}%]`}</Box>
            {gameCode ? (
              <Box
                as="span"
                bg="#ef4444"
                color="white"
                px="5px"
                py="1px"
                borderRadius="2px"
                fontSize="12px"
                fontWeight="700"
                lineHeight="1.2"
              >
                {gameCode}
              </Box>
            ) : null}
            {(points || points === 0 || points === '0') ? (
              <Box as="span" color="#1d4ed8" fontWeight="700">{`[${points}]`}</Box>
            ) : null}
          </Box>
        );
      },
    },
    {
      Header: 'H/L',
      accessor: '__hl',
      defaultWidth: 58,
      Cell: ({ row }) => {
        if (row.original.type !== 'player') return null;
        const manualNtp = String(
          row.original.playerInfo?.manual_ntp || row.original.manual_ntp || 'AUTO'
        ).toUpperCase();
        let label = 'A';
        let color = '#64748b';
        if (manualNtp === 'HIGH') {
          label = 'H';
          color = '#16a34a';
        } else if (manualNtp === 'LOW') {
          label = 'L';
          color = '#dc2626';
        } else if (manualNtp === 'LOWEST') {
          label = 'L';
          color = '#dc2626';
        } else if (manualNtp === 'WIN') {
          label = 'W';
          color = '#2563eb';
        } else if (manualNtp === 'AUTO') {
          label = 'A';
          color = '#64748b';
        }
        return (
          <Box as="span" fontWeight="700" color={color}>
            {label}
          </Box>
        );
      },
    },
    {
      Header: 'Sale',
      accessor: 'Sales',
      defaultWidth: 72,
      Cell: ({ row }) => {
        const sales = row.original.Sales;
        if (row.original.type !== 'player') {
          return <Box as="span" fontWeight="700">{sales}</Box>;
        }
        const playerId = row.original._id;
        const ticketId = row.original.latest_ticket_id || '0';
        const fDate = startDate ? formatDateLocal(startDate) : '';
        const tDate = endDate ? formatDateLocal(endDate) : '';
        const to = `/${slug || 'root'}/reports/tktdetails?UserID=${encodeURIComponent(playerId)}&TicketID=${encodeURIComponent(ticketId)}&FDate=${fDate}&TDate=${tDate}`;
        return (
          <Link to={to} style={{ color: 'blue', textDecoration: 'underline', fontWeight: 700 }}>
            {sales}
          </Link>
        );
      },
    },
    { Header: 'Win', accessor: 'win', defaultWidth: 68 },
    { Header: 'Gross', accessor: 'gross', defaultWidth: 72 },
    { Header: 'Player Comm', accessor: 'playerComm', defaultWidth: 88 },
    { Header: 'N2P', accessor: 'n2p', defaultWidth: 68 },
    { Header: 'Perc', accessor: 'current_perc', defaultWidth: 55 },
    { Header: 'Balance', accessor: 'balance', defaultWidth: 78 },
    {
      Header: 'Actions',
      accessor: '__actions',
      defaultWidth: 140,
      Cell: ({ row }) => (
        row.original.type === 'player' ? (
          <PlayerActionButtons player={row.original} refresh={refreshData} />
        ) : null
      ),
    },
  ], [data, playerliveTkt, refreshData, startDate, endDate, slug]);

  const visibleColumns = useMemo(
    () => filterColumns('reportSummary', columns),
    [columns, filterColumns]
  );

  return (
    <Box>
      <Heading mb={4}>Summary Report</Heading>

      <Flex
        gap={3}
        mb={4}
        align={{ base: 'stretch', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        wrap="wrap"
      >
        {isSuperAdmin && (
          <CompanySelect value={companyId} onChange={setCompanyId} width="100%" maxW={{ base: '100%', md: '220px' }} />
        )}
        <Select
          width="100%"
          maxW={{ base: '100%', md: '220px' }}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        >
          {filterOptions.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </Select>

        {filter === 'Custom Range' && (
          <Flex
            gap={3}
            direction={{ base: 'column', sm: 'row' }}
            width={{ base: '100%', md: 'auto' }}
            flexWrap="wrap"
          >
            <Box width={{ base: '100%', sm: '220px' }}>
              <DatePicker
                selected={customRange[0]}
                onChange={(date) => setCustomRange([date, customRange[1]])}
                placeholderText="Start Date"
                selectsStart
                startDate={customRange[0]}
                endDate={customRange[1]}
                customInput={<ResponsiveDateInput placeholder="Start Date" />}
              />
            </Box>
            <Box width={{ base: '100%', sm: '220px' }}>
              <DatePicker
                selected={customRange[1]}
                onChange={(date) => setCustomRange([customRange[0], date])}
                placeholderText="End Date"
                selectsEnd
                startDate={customRange[0]}
                endDate={customRange[1]}
                minDate={customRange[0]}
                customInput={<ResponsiveDateInput placeholder="End Date" />}
              />
            </Box>
          </Flex>
        )}
      </Flex>

      {loading ? (
        <Box>Loading...</Box>
      ) : error ? (
        <Box>Error: {error}</Box>
      ) : !companyId ? (
        <Box>
          {isSuperAdmin
            ? 'Please select a company to view this report.'
            : 'Unable to resolve your company. Please sign in again.'}
        </Box>
      ) : Array.isArray(data) && data.length > 0 ? (
        <GenericTable
          columns={visibleColumns}
          data={data}
          getRowStyle={(row) => {
            const { type, loggedIn } = row;

            if (type === 'total') {
              return {
                backgroundColor: '#000',
                color: '#ffff',
                fontWeight: 'bold',
              };
            }

            if (type === 'superstokez') {
              return {
                backgroundColor: 'navy',
                color: 'white',
                fontWeight: 'bold',
              };
            }

            if (type === 'player' && loggedIn) {
              return {
                backgroundColor: '#00ff00',
              };
            }

            return {};
          }}
          showColumnFilters={false}
          disableSort
          dense
          fontSize="13px"
          compactOnMobile
        />
      ) : (
        <Box>No data available for this period.</Box>
      )}
    </Box>
  );
};

export default SummaryReport;
